import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { COURSES, TESTIMONIALS, FAQS, openWhatsApp } from '../data/siteData';
import { 
  GraduationCap, 
  Star, 
  Check, 
  Phone, 
  Mail, 
  MapPin, 
  ChevronRight,
  Menu,
  X,
  MessageCircle,
  BookOpen,
  Target,
  Users,
  Award,
  Building2,
  Play
} from 'lucide-react';

const BookingDialog = lazy(() => import('../components/BookingDialog'));

// Lazy video component — only loads video when user clicks play
const LazyVideo = ({ poster, src, label }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  
  const handlePlay = () => {
    setPlaying(true);
  };

  useEffect(() => {
    if (playing && videoRef.current) {
      videoRef.current.play();
    }
  }, [playing]);

  if (!playing) {
    return (
      <div className="w-full h-full relative cursor-pointer group" onClick={handlePlay} role="button" aria-label={`Play ${label} video`}>
        <img src={`${poster}&w=400&q=60`} alt={label} className="w-full h-full object-cover" loading="lazy" decoding="async" width="400" height="711" />
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-colors">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <Play className="h-6 w-6 text-primary ml-0.5" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <video 
      ref={videoRef}
      className="w-full h-full object-cover"
      controls
      playsInline
      preload="metadata"
      poster={`${poster}&w=400&q=60`}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

// Logo component
const Logo = ({ className = "h-10" }) => (
  <div className="flex items-center">
    <img 
      src="/logo.webp" 
      alt="Anand Sir Online" 
      className={className}
      width="160"
      height="44"
    />
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const courses = COURSES;
  const testimonials = TESTIMONIALS;
  const faqs = FAQS;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    course_interest: '',
    message: ''
  });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const msg = `Hi, I'd like to get in touch.\nName: ${contactForm.name}\nEmail: ${contactForm.email}\nPhone: ${contactForm.phone}\nCourse Interest: ${contactForm.course_interest}\nMessage: ${contactForm.message}`;
    openWhatsApp(msg);
    toast.success('Redirecting to WhatsApp!');
    setContactForm({ name: '', email: '', phone: '', course_interest: '', message: '' });
  };

  const categories = [...new Set(courses.map(c => c.category))];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <Logo className="h-9 sm:h-11" />
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              <a href="#about" className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-primary hover:bg-primary/5 rounded-full transition-all duration-200" data-testid="nav-about">About</a>
              <a href="#courses" className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-primary hover:bg-primary/5 rounded-full transition-all duration-200" data-testid="nav-courses">Courses</a>
              <a href="#pricing" className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-primary hover:bg-primary/5 rounded-full transition-all duration-200" data-testid="nav-pricing">Pricing</a>
              <a href="#testimonials" className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-primary hover:bg-primary/5 rounded-full transition-all duration-200" data-testid="nav-testimonials">Testimonials</a>
              <a href="#faq" className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-primary hover:bg-primary/5 rounded-full transition-all duration-200" data-testid="nav-faq">FAQ</a>
              <a href="#contact" className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-primary hover:bg-primary/5 rounded-full transition-all duration-200" data-testid="nav-contact">Contact</a>
              <a href="/virtual-office" className="px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-full transition-all duration-200" data-testid="nav-virtual-office">Virtual Office</a>
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Button size="sm" className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 text-white font-semibold px-5 rounded-full shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/30 transition-all duration-300" onClick={() => setDemoDialogOpen(true)} data-testid="book-demo-cta">
                Book Demo
              </Button>
            </div>

            {/* Mobile menu button */}
            <button className="lg:hidden p-2.5 hover:bg-slate-100 rounded-full transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
        </div>
      )}

      {/* Mobile Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-[300px] sm:w-[340px] bg-white z-[70] transform transition-transform duration-300 ease-out lg:hidden shadow-2xl ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
            <Logo className="h-9" />
            <button onClick={() => setMobileMenuOpen(false)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </div>

          {/* Sidebar Navigation */}
          <div className="flex-1 overflow-y-auto py-6">
            <nav className="px-4 space-y-1">
              <a href="#about" className="flex items-center gap-3 px-4 py-3.5 text-base font-semibold text-slate-700 hover:bg-primary/5 hover:text-primary rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                <Users className="h-5 w-5 text-primary" />
                About
              </a>
              <a href="#courses" className="flex items-center gap-3 px-4 py-3.5 text-base font-semibold text-slate-700 hover:bg-primary/5 hover:text-primary rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                <BookOpen className="h-5 w-5 text-primary" />
                Courses
              </a>
              <a href="#pricing" className="flex items-center gap-3 px-4 py-3.5 text-base font-semibold text-slate-700 hover:bg-primary/5 hover:text-primary rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                <Target className="h-5 w-5 text-primary" />
                Pricing
              </a>
              <a href="#testimonials" className="flex items-center gap-3 px-4 py-3.5 text-base font-semibold text-slate-700 hover:bg-primary/5 hover:text-primary rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                <Star className="h-5 w-5 text-primary" />
                Testimonials
              </a>
              <a href="#faq" className="flex items-center gap-3 px-4 py-3.5 text-base font-semibold text-slate-700 hover:bg-primary/5 hover:text-primary rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                <MessageCircle className="h-5 w-5 text-primary" />
                FAQ
              </a>
              <a href="#contact" className="flex items-center gap-3 px-4 py-3.5 text-base font-semibold text-slate-700 hover:bg-primary/5 hover:text-primary rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                <Phone className="h-5 w-5 text-primary" />
                Contact
              </a>
              <a href="/virtual-office" className="flex items-center gap-3 px-4 py-3.5 text-base font-semibold text-white bg-primary rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                <Building2 className="h-5 w-5 text-white" />
                Virtual Office
              </a>
            </nav>

            {/* Popular Courses */}
            <div className="px-4 mt-8">
              <p className="px-4 text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Popular Courses</p>
              <nav className="space-y-1">
                <Link to="/course/sat-math" className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-primary hover:bg-primary/5 rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                  SAT Mathematics
                </Link>
                <Link to="/course/gre-quant" className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-primary hover:bg-primary/5 rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                  GRE Quantitative
                </Link>
                <Link to="/course/ib-math-aa-hl" className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-primary hover:bg-primary/5 rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                  IB Math AA HL
                </Link>
                <Link to="/course/cbse-11-12" className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-primary hover:bg-primary/5 rounded-xl transition-all" onClick={() => setMobileMenuOpen(false)}>
                  CBSE Math (11-12)
                </Link>
              </nav>
            </div>
          </div>

          {/* Sidebar Footer */}
          <div className="p-5 border-t border-slate-100 bg-slate-50/50 space-y-3">
            <Button className="w-full bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 font-semibold shadow-md" onClick={() => { setMobileMenuOpen(false); setDemoDialogOpen(true); }}>
              Book Free Demo
            </Button>
            <button onClick={() => { setMobileMenuOpen(false); openWhatsApp(); }} className="flex items-center justify-center gap-2 w-full py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-semibold transition-colors shadow-md">
              <MessageCircle className="h-4 w-4" />
              Chat on WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] sm:min-h-screen flex items-center pt-14 sm:pt-16 hero-bg" data-testid="hero-section">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-24">
          <div className="max-w-3xl">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-white/80 mb-3 sm:mb-4">Expert Mathematics Tuition</p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight sm:tracking-tighter font-serif font-semibold text-white mb-4 sm:mb-6 leading-tight">
              Master Mathematics with Personalized Guidance
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-white/80 mb-6 sm:mb-8 leading-relaxed max-w-2xl">
              One-on-one tuition for SAT, GRE, GMAT, AP, IB, CBSE & ISC. Unlock your mathematical potential with expert coaching tailored to your learning style.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-sm sm:text-base" onClick={() => setDemoDialogOpen(true)} data-testid="hero-book-demo">
                Book Free Demo <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-sm sm:text-base" asChild>
                <a href="#courses">Explore Courses</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee Ribbon */}
      <div className="bg-primary py-3 sm:py-4 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {['SAT Math', 'GRE Quant', 'GMAT Quant', 'AP Calculus AB', 'AP Calculus BC', 'IB Math AA', 'IB Math AI', 'CBSE Math', 'ISC Math', 'SAT Math', 'GRE Quant', 'GMAT Quant', 'AP Calculus AB', 'AP Calculus BC', 'IB Math AA', 'IB Math AI', 'CBSE Math', 'ISC Math'].map((item, i) => (
            <span key={i} className="text-white/80 text-xs sm:text-sm mx-4 sm:mx-8 font-medium">{item}</span>
          ))}
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center" data-testid="stat-students">
              <div className="flex justify-center mb-3 sm:mb-4">
                <Users className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              </div>
              <p className="text-3xl sm:text-4xl font-serif font-semibold text-primary">5000+</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">Students Taught</p>
            </div>
            <div className="text-center" data-testid="stat-experience">
              <div className="flex justify-center mb-3 sm:mb-4">
                <Award className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              </div>
              <p className="text-3xl sm:text-4xl font-serif font-semibold text-primary">33+</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">Years Experience</p>
            </div>
            <div className="text-center" data-testid="stat-success">
              <div className="flex justify-center mb-3 sm:mb-4">
                <Target className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              </div>
              <p className="text-3xl sm:text-4xl font-serif font-semibold text-primary">95%</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">Success Rate</p>
            </div>
            <div className="text-center" data-testid="stat-courses">
              <div className="flex justify-center mb-3 sm:mb-4">
                <BookOpen className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              </div>
              <p className="text-3xl sm:text-4xl font-serif font-semibold text-primary">13+</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">Courses Offered</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-24 bg-background" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-primary mb-3 sm:mb-4">About the Educator</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-serif font-semibold text-primary">Meet Mr. Anand Pal Singh</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Image */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-3 bg-gradient-to-br from-primary/20 to-blue-400/20 rounded-2xl blur-xl"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_math-mastery-hub-8/artifacts/e2zvdnwl_WhatsApp%20Image%202026-04-03%20at%2015.55.28.jpeg"
                  alt="Mr. Anand Pal Singh - Mathematics Educator"
                  className="relative w-full rounded-2xl shadow-2xl object-cover"
                  loading="lazy"
                  decoding="async"
                  width="448"
                  height="560"
                  data-testid="about-image"
                />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-5">
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                With over <span className="font-semibold text-primary">33 years of dedicated teaching experience</span> and having mentored <span className="font-semibold text-primary">5,000+ students</span>, I focus on building strong conceptual clarity, problem-solving skills, and a genuine love for Mathematics through logical thinking, rigorous practice, and exam-specific strategies.
              </p>

              <div className="space-y-2.5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <p className="text-sm text-slate-600">Extensive experience teaching diverse student groups across national and international curricula</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <p className="text-sm text-slate-600">Proven success in improving scores in SAT, AP Calculus, IB Mathematics, and board examinations</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <p className="text-sm text-slate-600">Skilled at differentiating instruction to meet the needs of students at various proficiency levels</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <p className="text-sm text-slate-600">Collaborates with school counsellors and faculty to support academic planning and university admissions</p>
                </div>
              </div>

              <p className="text-sm text-slate-600 leading-relaxed italic border-l-4 border-primary/30 pl-4">
                I am passionate about empowering students with the mathematical skills and confidence they need to excel in their academic journey and future endeavors.
              </p>

              <Button size="lg" className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 text-white font-semibold rounded-full shadow-md shadow-primary/25" onClick={() => setDemoDialogOpen(true)} data-testid="about-book-demo">
                Book a Free Demo Class <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-16 sm:py-24 bg-white defer-render" data-testid="courses-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-primary mb-3 sm:mb-4">Our Programs</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-serif font-semibold text-primary">Courses We Offer</h2>
          </div>

          {categories.map(category => (
            <div key={category} className="mb-8 sm:mb-12">
              <h3 className="text-lg sm:text-xl lg:text-2xl font-serif font-semibold text-primary mb-4 sm:mb-6 border-b border-border pb-3 sm:pb-4">{category}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {courses.filter(c => c.category === category).map(course => (
                  <Card key={course.id} className="card-hover border border-border cursor-pointer" data-testid={`course-card-${course.id}`} onClick={() => navigate(`/course/${course.id}`)}>
                    <CardHeader className="pb-2 sm:pb-4">
                      <CardTitle className="font-serif text-base sm:text-lg lg:text-xl">{course.name}</CardTitle>
                      <CardDescription className="text-muted-foreground text-xs sm:text-sm">{course.duration}</CardDescription>
                    </CardHeader>
                    <CardContent className="pb-3 sm:pb-4">
                      <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 line-clamp-2">{course.description}</p>
                      {course.highlights && (
                        <div className="flex flex-wrap gap-1 sm:gap-2 mb-3">
                          {course.highlights.slice(0, 2).map((highlight, i) => (
                            <span key={i} className="bg-primary/10 text-primary px-2 py-0.5 rounded text-[10px] sm:text-xs">
                              {highlight}
                            </span>
                          ))}
                        </div>
                      )}
                      <ul className="space-y-1 sm:space-y-2">
                        {course.features.slice(0, 2).map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs sm:text-sm">
                            <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
                            <span className="line-clamp-1">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardFooter className="flex justify-between items-center border-t border-border pt-3 sm:pt-4">
                      <div>
                        <span className="text-lg sm:text-xl font-serif font-semibold text-primary">₹2,000</span>
                        <span className="text-xs sm:text-sm text-muted-foreground">/hr</span>
                      </div>
                      <Button variant="outline" size="sm" className="text-xs sm:text-sm" onClick={(e) => { e.stopPropagation(); navigate(`/course/${course.id}`); }}>View Details</Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 bg-white defer-render" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-primary mb-3 sm:mb-4">Pricing</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-serif font-semibold text-primary mb-3 sm:mb-4">Simple, Transparent Pricing</h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">One-on-one personalized tutoring with flexible scheduling. Pay per hour or save with our package deals.</p>
          </div>

          {/* Hourly Rates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto mb-8 sm:mb-12">
            {/* Online Classes */}
            <Card className="border-2 border-primary relative" data-testid="pricing-online">
              <CardHeader className="text-center pb-2">
                <div className="flex justify-center mb-3">
                  <Users className="h-10 w-10 sm:h-12 sm:w-12 text-primary" />
                </div>
                <CardTitle className="font-serif text-xl sm:text-2xl">Online Classes</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Via Zoom / Google Meet</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-4xl sm:text-5xl font-serif font-semibold text-primary my-4 sm:my-6">₹2,000<span className="text-sm sm:text-lg text-muted-foreground">/hour</span></p>
                <ul className="space-y-2 sm:space-y-3 text-left">
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />1-on-1 personalized attention</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />Flexible scheduling</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />Screen sharing & digital whiteboard</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />Session recordings available</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />Study materials provided</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />WhatsApp doubt support</li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-primary hover:bg-primary/90 text-sm" onClick={() => setDemoDialogOpen(true)}>Book Free Demo</Button>
              </CardFooter>
            </Card>

            {/* Offline / Home Visit */}
            <Card className="border-2 border-green-600" data-testid="pricing-offline">
              <CardHeader className="text-center pb-2">
                <div className="flex justify-center mb-3">
                  <MapPin className="h-10 w-10 sm:h-12 sm:w-12 text-green-600" />
                </div>
                <CardTitle className="font-serif text-xl sm:text-2xl">Home Tuition</CardTitle>
                <CardDescription className="text-xs sm:text-sm">In-Person at Your Location</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-4xl sm:text-5xl font-serif font-semibold text-green-600 my-4 sm:my-6">₹3,000<span className="text-sm sm:text-lg text-muted-foreground">/hour</span></p>
                <ul className="space-y-2 sm:space-y-3 text-left">
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Face-to-face interaction</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Home tuition at your convenience</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Hands-on problem solving</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Physical study materials</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Travel to your location</li>
                  <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Distraction-free environment</li>
                </ul>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full text-sm border-green-600 text-green-600 hover:bg-green-50" onClick={() => setDemoDialogOpen(true)}>Book Free Demo</Button>
              </CardFooter>
            </Card>
          </div>

          {/* Package Deals */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl sm:text-2xl font-serif font-semibold text-primary text-center mb-6 sm:mb-8">Save More with Package Deals</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {/* Online Package */}
              <Card className="border-2 border-primary/50 bg-primary/5 relative" data-testid="pricing-online-package">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap">Save ₹10,000</div>
                <CardHeader className="text-center pb-2">
                  <CardTitle className="font-serif text-lg sm:text-xl">Online Package</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">30 Classes Bundle</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-2">
                    <span className="text-lg text-muted-foreground line-through">₹60,000</span>
                  </div>
                  <p className="text-3xl sm:text-4xl font-serif font-semibold text-primary mb-2">₹50,000</p>
                  <p className="text-xs sm:text-sm text-primary font-medium mb-4">₹1,667/class (17% off)</p>
                  <ul className="space-y-2 text-left">
                    <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />30 one-hour sessions</li>
                    <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />Valid for 6 months</li>
                    <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />Priority scheduling</li>
                    <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary flex-shrink-0" />Free study materials</li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className="w-full bg-primary hover:bg-primary/90 text-sm" onClick={() => setDemoDialogOpen(true)}>Get Started</Button>
                </CardFooter>
              </Card>

              {/* Home Tuition Package */}
              <Card className="border-2 border-green-600/50 bg-green-50 relative" data-testid="pricing-offline-package">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white px-3 sm:px-4 py-1 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap">Save ₹15,000</div>
                <CardHeader className="text-center pb-2">
                  <CardTitle className="font-serif text-lg sm:text-xl">Home Tuition Package</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">30 Classes Bundle</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-2">
                    <span className="text-lg text-muted-foreground line-through">₹90,000</span>
                  </div>
                  <p className="text-3xl sm:text-4xl font-serif font-semibold text-green-600 mb-2">₹75,000</p>
                  <p className="text-xs sm:text-sm text-green-600 font-medium mb-4">₹2,500/class (17% off)</p>
                  <ul className="space-y-2 text-left">
                    <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />30 one-hour sessions</li>
                    <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Valid for 6 months</li>
                    <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Priority scheduling</li>
                    <li className="flex items-center gap-2 text-xs sm:text-sm"><Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />Free study materials</li>
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full text-sm border-green-600 text-green-600 hover:bg-green-100" onClick={() => setDemoDialogOpen(true)}>Get Started</Button>
                </CardFooter>
              </Card>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 sm:mt-12 text-center">
            <p className="text-xs sm:text-sm text-muted-foreground mb-4">All courses include: Personalized curriculum • Progress tracking • Regular assessments • Exam preparation</p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-primary" />
                <span>No joining fee</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-primary" />
                <span>Pay as you go</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-primary" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Testimonials Section */}
      <section id="testimonials" className="py-16 sm:py-24 bg-slate-50 defer-render" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-primary mb-3 sm:mb-4">Success Stories</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-serif font-semibold text-primary mb-3 sm:mb-4">What Our Students Say</h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">Hear directly from our students about their learning experience and success stories.</p>
          </div>

          {/* Video Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Video Testimonial 1 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300" data-testid="testimonial-0">
              <div className="aspect-[9/16] relative">
                <LazyVideo 
                  poster="https://images.unsplash.com/photo-1758525861622-f4e7ac86a2d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHx0ZWVuYWdlciUyMHN0dWRlbnQlMjBzdHVkeWluZyUyMGRlc2slMjBoaWdoJTIwc2Nob29sfGVufDB8fHx8MTc3NTIwNzU5Nnww&ixlib=rb-4.1.0&q=85"
                  src="https://customer-assets.emergentagent.com/job_math-mastery-hub-8/artifacts/2df4fmyq_WhatsApp%20Video%202026-04-03%20at%2012.47.11%20%281%29.mp4"
                  label="SAT Mathematics student testimonial"
                />
              </div>
              <div className="p-4">
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="font-semibold text-slate-800">Student Testimonial</p>
                <p className="text-sm text-muted-foreground">SAT Mathematics</p>
              </div>
            </div>

            {/* Video Testimonial 2 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300" data-testid="testimonial-1">
              <div className="aspect-[9/16] relative">
                <LazyVideo 
                  poster="https://images.unsplash.com/photo-1598981457915-aea220950616?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHx0ZWVuYWdlciUyMHN0dWRlbnQlMjBzdHVkeWluZyUyMGRlc2slMjBoaWdoJTIwc2Nob29sfGVufDB8fHx8MTc3NTIwNzU5Nnww&ixlib=rb-4.1.0&q=85"
                  src="https://customer-assets.emergentagent.com/job_math-mastery-hub-8/artifacts/rb8dyeg0_WhatsApp%20Video%202026-04-03%20at%2012.47.11.mp4"
                  label="IB Mathematics student testimonial"
                />
              </div>
              <div className="p-4">
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="font-semibold text-slate-800">Student Testimonial</p>
                <p className="text-sm text-muted-foreground">IB Mathematics</p>
              </div>
            </div>

            {/* Video Testimonial 3 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300" data-testid="testimonial-2">
              <div className="aspect-[9/16] relative">
                <LazyVideo 
                  poster="https://images.unsplash.com/photo-1667552094158-d17f4d2210e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxjb2xsZWdlJTIwc3R1ZGVudCUyMHlvdW5nJTIwYWR1bHQlMjBsZWFybmluZyUyMGJvb2tzfGVufDB8fHx8MTc3NTIwNzU5Nnww&ixlib=rb-4.1.0&q=85"
                  src="https://customer-assets.emergentagent.com/job_math-mastery-hub-8/artifacts/081w5u4e_WhatsApp%20Video%202026-04-03%20at%2012.47.13%20%281%29.mp4"
                  label="GRE Quantitative student testimonial"
                />
              </div>
              <div className="p-4">
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="font-semibold text-slate-800">Student Testimonial</p>
                <p className="text-sm text-muted-foreground">GRE Quantitative</p>
              </div>
            </div>

            {/* Video Testimonial 4 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300" data-testid="testimonial-3">
              <div className="aspect-[9/16] relative">
                <LazyVideo 
                  poster="https://images.unsplash.com/photo-1656266724105-302774929dfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxjb2xsZWdlJTIwc3R1ZGVudCUyMHlvdW5nJTIwYWR1bHQlMjBsZWFybmluZyUyMGJvb2tzfGVufDB8fHx8MTc3NTIwNzU5Nnww&ixlib=rb-4.1.0&q=85"
                  src="https://customer-assets.emergentagent.com/job_math-mastery-hub-8/artifacts/6qeqcsry_WhatsApp%20Video%202026-04-03%20at%2012.47.13%20%282%29.mp4"
                  label="CBSE Mathematics student testimonial"
                />
              </div>
              <div className="p-4">
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="font-semibold text-slate-800">Student Testimonial</p>
                <p className="text-sm text-muted-foreground">CBSE Mathematics</p>
              </div>
            </div>

            {/* Video Testimonial 5 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300" data-testid="testimonial-4">
              <div className="aspect-[9/16] relative">
                <LazyVideo 
                  poster="https://images.unsplash.com/photo-1741699428220-65f37f3fbbcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHx5b3VuZyUyMGFkdWx0JTIwc3R1ZHlpbmclMjBsaWJyYXJ5JTIwbGFwdG9wfGVufDB8fHx8MTc3NTIwNzgyN3ww&ixlib=rb-4.1.0&q=85"
                  src="https://customer-assets.emergentagent.com/job_math-mastery-hub-8/artifacts/29h5psbp_WhatsApp%20Video%202026-04-03%20at%2012.47.13.mp4"
                  label="AP Calculus student testimonial"
                />
              </div>
              <div className="p-4">
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="font-semibold text-slate-800">Student Testimonial</p>
                <p className="text-sm text-muted-foreground">AP Calculus</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-24 bg-white defer-render" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-primary mb-3 sm:mb-4">FAQ</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-serif font-semibold text-primary">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={faq.id} value={faq.id} data-testid={`faq-item-${index}`}>
                <AccordionTrigger className="text-left font-medium hover:no-underline text-sm sm:text-base py-3 sm:py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-xs sm:text-sm pb-3 sm:pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-24 bg-primary defer-render" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            <div>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-semibold text-white/80 mb-3 sm:mb-4">Get in Touch</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-serif font-semibold text-white mb-4 sm:mb-6">Ready to Excel in Mathematics?</h2>
              <p className="text-white/80 mb-6 sm:mb-8 text-sm sm:text-base">Contact us today to start your journey towards mathematical excellence. We're here to help you achieve your academic goals.</p>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="bg-white/10 p-2 sm:p-3 rounded-lg">
                    <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs sm:text-sm">Phone</p>
                    <p className="text-white text-sm sm:text-base">+91 98101 34293</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="bg-white/10 p-2 sm:p-3 rounded-lg">
                    <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs sm:text-sm">Email</p>
                    <p className="text-white text-sm sm:text-base">jmeducarepvtltd@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="bg-white/10 p-2 sm:p-3 rounded-lg">
                    <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs sm:text-sm">Location</p>
                    <p className="text-white text-sm sm:text-base">Delhi, India (Online & Offline)</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
                <Button variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-primary text-xs sm:text-sm" onClick={openWhatsApp}>
                  <MessageCircle className="mr-2 h-4 w-4 sm:h-5 sm:w-5" /> WhatsApp
                </Button>
              </div>
            </div>

            <Card className="bg-white" data-testid="contact-form-card">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="font-serif text-xl sm:text-2xl">Send us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-3 sm:space-y-4">
                  <Input placeholder="Full Name" className="text-sm" value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} required data-testid="contact-name-input" />
                  <Input type="email" placeholder="Email" className="text-sm" value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} required data-testid="contact-email-input" />
                  <Input placeholder="Phone Number" className="text-sm" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} required data-testid="contact-phone-input" />
                  <Select value={contactForm.course_interest} onValueChange={(value) => setContactForm({...contactForm, course_interest: value})}>
                    <SelectTrigger className="text-sm" data-testid="contact-course-select">
                      <SelectValue placeholder="Course Interest" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.name}>{course.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea placeholder="Your Message" rows={3} className="text-sm" value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} required data-testid="contact-message-input" />
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-sm" data-testid="contact-submit-btn">Send Message</Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary border-t border-white/10 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <img src="/logo.webp" alt="Anand Sir Online" className="h-8 sm:h-10" loading="lazy" decoding="async" width="120" height="40" />
              </div>
              <p className="text-white/60 text-xs sm:text-sm">Expert mathematics tuition for standardized tests, AP, IB, and board exams.</p>
            </div>
            <div>
              <h4 className="font-medium text-white mb-3 sm:mb-4 text-sm sm:text-base">Quick Links</h4>
              <ul className="space-y-1 sm:space-y-2">
                <li><a href="#courses" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors">Courses</a></li>
                <li><a href="#pricing" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors">Pricing</a></li>
                <li><a href="#testimonials" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors">Testimonials</a></li>
                <li><a href="#faq" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-white mb-3 sm:mb-4 text-sm sm:text-base">Courses</h4>
              <ul className="space-y-1 sm:space-y-2">
                <li><Link to="/course/sat-math" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">SAT Mathematics</Link></li>
                <li><Link to="/course/gre-quant" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">GRE Quantitative</Link></li>
                <li><Link to="/course/ib-math-aa-hl" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">IB Mathematics</Link></li>
                <li><Link to="/course/cbse-11-12" className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">CBSE/ISC Math</Link></li>
              </ul>
            </div>
            <div className="col-span-2 md:col-span-1">
              <h4 className="font-medium text-white mb-3 sm:mb-4 text-sm sm:text-base">Connect</h4>
              <div className="flex gap-3 sm:gap-4">
                <a href="https://www.instagram.com/anandsironlinedotcom" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="Instagram" data-testid="social-instagram">
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="https://www.youtube.com/@anandsironlinedotcom" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="YouTube" data-testid="social-youtube">
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                <a href="https://x.com/AnandPa56921556" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="X (Twitter)" data-testid="social-twitter">
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a href="https://www.linkedin.com/in/anand-pal-singh-sudan-b5977b136/" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="LinkedIn" data-testid="social-linkedin">
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center">
            <p className="text-white/60 text-xs sm:text-sm">&copy; {new Date().getFullYear()} JM Educare Pvt. Ltd. (Anand Sir Online). All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Lazy-loaded Booking Dialog */}
      {demoDialogOpen && (
        <Suspense fallback={null}>
          <BookingDialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen} courses={courses} />
        </Suspense>
      )}

      {/* WhatsApp Floating Button - positioned above Made with Emergent */}
      <button onClick={openWhatsApp} className="fixed bottom-4 left-4 sm:bottom-8 sm:left-8 z-40 bg-green-500 hover:bg-green-600 text-white p-3 sm:p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-110" aria-label="Chat on WhatsApp" data-testid="whatsapp-float-btn">
        <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6" />
      </button>
    </div>
  );
}
